import { createFileRoute, Link } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/kit')({
  component: KitPage,
})

type KitItem = {
  id: string
  category: string
  brand?: string
  productName?: string
  colorFamily?: string
  finish?: string
  coverage?: string
  intensity?: string
  openedAt?: string
  status: 'active' | 'used_up' | 'expired' | 'decluttered'
  createdAt: string
  updatedAt: string
}

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'concealer', label: 'Concealer' },
  { value: 'powder', label: 'Powder' },
  { value: 'blush', label: 'Blush' },
  { value: 'bronzer', label: 'Bronzer' },
  { value: 'highlighter', label: 'Highlight' },
  { value: 'eyeshadow', label: 'Shadow' },
  { value: 'eyeliner', label: 'Liner' },
  { value: 'mascara', label: 'Mascara' },
  { value: 'brow', label: 'Brow' },
  { value: 'lipstick', label: 'Lip' },
  { value: 'primer', label: 'Primer' },
  { value: 'skincare', label: 'Skin prep' },
  { value: 'tool', label: 'Tools' },
]

const STARTER_STEPS = [
  'Add your complexion base first',
  'Tag shade families, not exact skin tones',
  'Use scan results only for cosmetic matching',
]

const SAMPLE_KIT = [
  ['Base', '#BDE0FE', 'Skin tint / sheer'],
  ['Blush', '#FFC8DD', 'Rose cream'],
  ['Bronze', '#E9B28E', 'Warm satin'],
  ['Eyes', '#CDB4DB', 'Soft plum'],
  ['Lip', '#FFAFCC', 'Berry balm'],
] as const

function KitPage() {
  const [KitProductCard, setKitProductCard] = React.useState<React.ComponentType<
    Record<string, unknown>
  > | null>(null)
  const [AppButton, setAppButton] = React.useState<React.ComponentType<
    Record<string, unknown>
  > | null>(null)
  const [, setAppDrawer] = React.useState<React.ComponentType<Record<string, unknown>> | null>(null)

  const [items, setItems] = React.useState<KitItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [category, setCategory] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [showAddDrawer, setShowAddDrawer] = React.useState(false)

  React.useEffect(() => {
    import('@ootopia/ui').then((m) => {
      setKitProductCard(() => m.KitProductCard as never)
      setAppButton(() => m.AppButton as never)
      setAppDrawer(() => m.AppDrawer as never)
    })
  }, [])

  const fetchItems = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const sessionRes = await fetch('/api/auth/session-state')
      const session = sessionRes.ok
        ? ((await sessionRes.json()) as { authenticated?: boolean })
        : { authenticated: false }
      if (session.authenticated !== true) {
        setItems([])
        return
      }

      const params = new URLSearchParams()
      if (category) params.set('category', category)
      if (search) params.set('search', search)

      const res = await fetch(`/api/v1/me/kit-items?${params}`)
      if (!res.ok) {
        throw new Error('Failed to fetch items')
      }
      const data = await res.json()
      setItems(data.items ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [category, search])

  React.useEffect(() => {
    fetchItems()
  }, [fetchItems])

  async function handleDelete(item: KitItem) {
    if (!confirm('Remove this item from your kit?')) return

    try {
      const res = await fetch(`/api/v1/me/kit-items/${item.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete')
      setItems((prev) => prev.filter((i) => i.id !== item.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  return (
    <main className="ootopia-pwa-page ootopia-pwa-kit grid gap-6 pb-10 lg:grid-cols-[0.38fr_0.62fr]">
      <aside
        className="ootopia-pwa-kit__hero ootopia-pwa-surface lg:sticky lg:top-24 lg:self-start"
        aria-labelledby="kit-title"
      >
        <div className="relative overflow-hidden">
          <div className="ootopia-orb-decor ootopia-orb-decor-1" aria-hidden="true" />
          <div className="relative z-10">
            <img
              src="/homepage/kit-inventory-board.png"
              alt="Shelf inventory with matched product swatches"
              className="ootopia-pwa-kit__hero-image"
              loading="eager"
              decoding="async"
            />
            <p className="ootopia-eyebrow">private shelf</p>
            <h1 id="kit-title" className="ootopia-pwa-title mt-4 max-w-sm">
              The palette you already own
            </h1>
            <p className="mt-5 text-pretty text-sm leading-7 text-foreground/64">
              Keep the shelf lean. Ootopia reads what is usable, what is close, and what is truly
              missing before suggesting a remix.
            </p>

            <div className="mt-8 grid gap-3">
              {STARTER_STEPS.map((step, index) => (
                <div
                  key={step}
                  className="flex items-start gap-3 rounded-[1.35rem] bg-white/66 p-3"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-foreground text-xs font-bold text-background">
                    {index + 1}
                  </span>
                  <p className="pt-1 text-sm font-medium text-foreground/72">{step}</p>
                </div>
              ))}
            </div>

            <Link to="/looks" className="mt-8 inline-flex text-sm font-semibold text-foreground/64">
              ← Back to looks
            </Link>
          </div>
        </div>
      </aside>

      <section className="ootopia-pwa-kit__shelf" aria-labelledby="kit-shelf-heading">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="ootopia-eyebrow">inventory</p>
            <h2 id="kit-shelf-heading" className="ootopia-pwa-title mt-2">
              Build the usable shelf.
            </h2>
          </div>
          {AppButton
            ? React.createElement(
                AppButton as never,
                {
                  color: 'primary',
                  size: 'md',
                  onPress: () => setShowAddDrawer(true),
                  className: 'ootopia-cta',
                } as never,
                '+ Add item',
              )
            : null}
        </div>

        <div className="ootopia-pwa-surface ootopia-pwa-kit__toolbar">
          <div className="space-y-5">
            <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
              <label className="block">
                <span className="sr-only">Search products</span>
                <input
                  type="text"
                  placeholder="Search brand, shade, finish..."
                  className="ootopia-pwa-field"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                />
              </label>
              <button type="button" className="ootopia-secondary-cta" onClick={fetchItems}>
                Search shelf
              </button>
            </div>

            <div className="ootopia-pwa-kit__categories">
              {CATEGORIES.map((cat) => (
                <button
                  type="button"
                  key={cat.value}
                  className={`ootopia-category-chip ${category === cat.value ? 'is-active' : ''}`}
                  onClick={() => setCategory(cat.value)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="ootopia-panel p-2">
            <div className="rounded-[1.45rem] bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
              {error}
            </div>
          </div>
        )}

        {loading && (
          <div className="ootopia-panel p-2">
            <div className="ootopia-panel-inner grid gap-3 p-5">
              <div className="h-4 w-36 rounded-full ootopia-shimmer" />
              <div className="h-24 rounded-[1.5rem] ootopia-shimmer" />
              <div className="h-24 rounded-[1.5rem] ootopia-shimmer" />
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="ootopia-panel p-2">
            <div className="ootopia-panel-inner relative overflow-hidden px-6 py-12 text-center sm:px-10 sm:py-16">
              <div className="ootopia-orb-decor ootopia-orb-decor-2" aria-hidden="true" />
              <div className="relative z-10 mx-auto max-w-xl">
                <div className="mx-auto mb-7 grid h-28 w-28 place-items-center rounded-[2rem] bg-gradient-to-br from-white to-pink-100 shadow-[0_24px_70px_oklch(0.62_0.14_12_/_0.22)]">
                  <span className="text-5xl" aria-hidden="true">
                    ◒
                  </span>
                </div>
                <p className="ootopia-eyebrow">empty shelf</p>
                <h3 className="mt-3 text-3xl font-black tracking-[-0.055em]">
                  Start with what you reach for most.
                </h3>
                <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-foreground/62">
                  Add the complexion, lip, and blush products you actually use. Ootopia remixes
                  against that shelf and flags only what is genuinely missing.
                </p>
                <div className="mx-auto mt-8 grid max-w-2xl gap-2 text-left sm:grid-cols-2">
                  {SAMPLE_KIT.map(([name, color, detail]) => (
                    <div
                      key={name}
                      className="flex items-center gap-3 rounded-[1.25rem] bg-white/70 p-3"
                    >
                      <span
                        className="h-10 w-10 rounded-[1rem]"
                        style={{
                          background: `radial-gradient(circle at 35% 25%, white, transparent 20%), ${color}`,
                        }}
                      />
                      <span>
                        <span className="block text-sm font-bold">{name}</span>
                        <span className="block text-xs text-foreground/52">{detail}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    type="button"
                    className="ootopia-cta group"
                    onClick={() => setShowAddDrawer(true)}
                  >
                    <span>Add first item</span>
                    <span className="ootopia-cta-mark" aria-hidden="true">
                      +
                    </span>
                  </button>
                  <Link to="/looks" className="ootopia-secondary-cta">
                    Browse looks
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid gap-3">
            {items.map((item) =>
              KitProductCard
                ? React.createElement(
                    KitProductCard as never,
                    {
                      key: item.id,
                      item,
                      onDelete: handleDelete,
                    } as never,
                  )
                : null,
            )}
          </div>
        )}
      </section>

      {showAddDrawer && (
        <div className="fixed inset-0 z-40 flex items-end bg-foreground/42 px-3 pb-3">
          <div className="ootopia-panel mx-auto w-full max-w-2xl p-2">
            <div className="ootopia-panel-inner p-6">
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <p className="ootopia-eyebrow">kit item</p>
                  <h3 className="mt-2 text-2xl font-black tracking-[-0.045em]">Add product</h3>
                </div>
                <button
                  type="button"
                  className="ootopia-secondary-cta"
                  onClick={() => setShowAddDrawer(false)}
                >
                  Close
                </button>
              </div>
              <AddKitItemForm
                onSuccess={() => {
                  setShowAddDrawer(false)
                  fetchItems()
                }}
                onCancel={() => setShowAddDrawer(false)}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

function AddKitItemForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [category, setCategory] = React.useState('')
  const [brand, setBrand] = React.useState('')
  const [productName, setProductName] = React.useState('')
  const [colorFamily, setColorFamily] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!category) {
      setError('Category is required')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const res = await fetch('/api/v1/me/kit-items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          brand: brand || undefined,
          productName: productName || undefined,
          colorFamily: colorFamily || undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error ?? 'Failed to create item')
      }

      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Creation failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-3">
      <label className="grid gap-1.5">
        <span className="text-sm font-semibold">Category *</span>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-12 rounded-[1rem] bg-white/72 px-3 text-sm outline-none ring-1 ring-foreground/8"
          required
        >
          <option value="">Select category</option>
          {CATEGORIES.filter((c) => c.value).map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-1.5">
        <span className="text-sm font-semibold">Brand</span>
        <input
          type="text"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          placeholder="Fenty Beauty"
          className="h-12 rounded-[1rem] bg-white/72 px-3 text-sm outline-none ring-1 ring-foreground/8"
        />
      </label>

      <label className="grid gap-1.5">
        <span className="text-sm font-semibold">Product name</span>
        <input
          type="text"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="Pro Filt'r Soft Matte"
          className="h-12 rounded-[1rem] bg-white/72 px-3 text-sm outline-none ring-1 ring-foreground/8"
        />
      </label>

      <label className="grid gap-1.5">
        <span className="text-sm font-semibold">Color family</span>
        <input
          type="text"
          value={colorFamily}
          onChange={(e) => setColorFamily(e.target.value)}
          placeholder="warm medium, rose, deep berry"
          className="h-12 rounded-[1rem] bg-white/72 px-3 text-sm outline-none ring-1 ring-foreground/8"
        />
      </label>

      {error && (
        <div className="rounded-[1rem] bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
      )}

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <button type="button" onClick={onCancel} className="ootopia-secondary-cta">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="ootopia-cta justify-center disabled:opacity-50"
        >
          {submitting ? 'Adding...' : 'Add to kit'}
        </button>
      </div>
    </form>
  )
}
